import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { exportApi, type ExportBatch, type ExportBatchFilters, type ExportGroup } from '../services/api';
import { useToast } from '../context/ToastContext';
import { Spinner } from '../components/shared/Spinner';
// `String(err)` affichait « ApiError: … » à l'utilisateur et perdait les
// `issues[]` d'une erreur Zod. Un écran qui génère des virements SEPA doit dire
// EXACTEMENT ce que le serveur a refusé.
import { formatApiError } from '../lib/api-error';
import { BANQUES_VIREMENT } from '../../server/lib/banques-virement';

/**
 * Module Paiements — écran « Exporter » (Valérie / admin).
 * Génère un fichier XML SEPA par combinaison (banque + type de charge) à partir
 * des paiements validés. Le fichier est ensuite ENREGISTRÉ dans le bon dossier
 * Dropbox de la banque (un seul bouton, pas de téléchargement navigateur :
 * Valérie récupère les fichiers dans Dropbox pour les déposer en banque), puis
 * marqué déposé avec une date de dépôt.
 */

function eur(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

const BATCH_STATUS: Record<string, string> = {
  generated: 'Généré',
  uploaded: 'Archivé Dropbox',
  completed: 'Déposé',
};

export function PaiementsExportPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [filters, setFilters] = useState<ExportBatchFilters>({});
  // Lot en cours de « Marquer déposé » (ouvre la saisie de date).
  const [depositFor, setDepositFor] = useState<ExportBatch | null>(null);
  const [depositDate, setDepositDate] = useState<string>(todayISO());

  const q = useQuery({
    queryKey: ['export', 'preview', filters],
    queryFn: () => exportApi.preview(filters),
    staleTime: 15_000,
  });

  const generateMutation = useMutation({
    mutationFn: (g: ExportGroup) => exportApi.generate(g.banque, g.type_charge, g.source),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['export'] });
      qc.invalidateQueries({ queryKey: ['paiements'] });
      toast.success(`Fichier généré : ${r.batch.xml_filename} (${r.batch.nb_transactions} virement(s)).`);
    },
    onError: (err) => toast.error(`Génération échouée : ${formatApiError(err)}`),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => exportApi.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['export'] });
      toast.success('Fichier enregistré dans Dropbox.');
    },
    onError: (err) => toast.error(`Enregistrement Dropbox échoué : ${formatApiError(err)}`),
  });

  const completeMutation = useMutation({
    mutationFn: (args: { id: string; date_depot: string }) => exportApi.complete(args.id, args.date_depot),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['export'] });
      qc.invalidateQueries({ queryKey: ['paiements'] });
      setDepositFor(null);
      toast.success(`Lot marqué déposé : ${r.paid} paiement(s) en « payé ».`);
    },
    onError: (err) => toast.error(`Échec : ${formatApiError(err)}`),
  });

  const groups = q.data?.groups ?? [];
  const batches = q.data?.batches ?? [];

  const setFilter = (patch: Partial<ExportBatchFilters>) =>
    setFilters((f) => {
      const next = { ...f, ...patch };
      // Retire les clés vides pour ne pas polluer la query string.
      (Object.keys(next) as (keyof ExportBatchFilters)[]).forEach((k) => {
        if (!next[k]) delete next[k];
      });
      return next;
    });

  const hasFilters = Object.keys(filters).length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Exporter les virements</h1>
        <p className="text-sm text-slate-500 mt-1">
          Un fichier XML SEPA par banque et type de charge, à enregistrer dans Dropbox puis à
          déposer sur le portail bancaire.
        </p>
      </div>

      {q.isLoading ? (
        <p className="text-slate-500">Chargement…</p>
      ) : q.isError ? (
        <p className="text-[#C0423F]">Erreur : {formatApiError(q.error)}</p>
      ) : (
        <>
          {/* Lots prêts à exporter */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 font-medium">
              Factures validées, fichiers bancaires prêts à exporter
            </div>
            {groups.length === 0 ? (
              <p className="px-4 py-6 text-center text-slate-400 italic">
                Aucun paiement validé en attente d'export.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-slate-500 text-left text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 font-medium">Type de charge</th>
                    <th className="px-4 py-2 font-medium">Banque</th>
                    <th className="px-4 py-2 font-medium text-right">Nb</th>
                    <th className="px-4 py-2 font-medium text-right">Montant</th>
                    <th className="px-4 py-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {groups.map((g, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2">{g.type_charge ?? 'Sans type de charge'}</td>
                      <td className="px-4 py-2">{g.banque}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{g.nb}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{eur(g.montant_total)}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => generateMutation.mutate(g)}
                          disabled={generateMutation.isPending}
                          className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          Générer XML
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Fichiers générés */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 font-medium">Fichiers générés</div>

            {/* Filtres */}
            <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-slate-100 text-sm">
              <label className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Statut</span>
                <select
                  value={filters.status ?? ''}
                  onChange={(e) => setFilter({ status: e.target.value })}
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                >
                  <option value="">Tous</option>
                  <option value="generated">Généré</option>
                  <option value="uploaded">Archivé Dropbox</option>
                  <option value="completed">Déposé</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Banque</span>
                <select
                  value={filters.banque ?? ''}
                  onChange={(e) => setFilter({ banque: e.target.value })}
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                >
                  <option value="">Toutes</option>
                  {BANQUES_VIREMENT.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Déposé du</span>
                <input
                  type="date"
                  value={filters.depot_from ?? ''}
                  onChange={(e) => setFilter({ depot_from: e.target.value })}
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">au</span>
                <input
                  type="date"
                  value={filters.depot_to ?? ''}
                  onChange={(e) => setFilter({ depot_to: e.target.value })}
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm"
                />
              </label>
              {hasFilters && (
                <button
                  type="button"
                  onClick={() => setFilters({})}
                  className="text-xs text-slate-600 hover:underline pb-1.5"
                >
                  Réinitialiser
                </button>
              )}
            </div>

            {batches.length === 0 ? (
              <p className="px-4 py-6 text-center text-slate-400 italic">
                {hasFilters ? 'Aucun fichier ne correspond aux filtres.' : 'Aucun fichier généré.'}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-slate-500 text-left text-xs uppercase">
                  <tr>
                    <th className="px-4 py-2 font-medium">Fichier</th>
                    <th className="px-4 py-2 font-medium">Banque</th>
                    <th className="px-4 py-2 font-medium text-right">Nb</th>
                    <th className="px-4 py-2 font-medium text-right">Montant</th>
                    <th className="px-4 py-2 font-medium">Statut</th>
                    <th className="px-4 py-2 font-medium">Date de dépôt</th>
                    <th className="px-4 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {batches.map((b) => (
                    <tr key={b.id}>
                      <td className="px-4 py-2 font-mono text-xs">{b.xml_filename}</td>
                      <td className="px-4 py-2 font-medium">{b.banque}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{b.nb_transactions}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{eur(b.montant_total)}</td>
                      <td className="px-4 py-2">
                        <span className="text-xs">{BATCH_STATUS[b.status] ?? b.status}</span>
                      </td>
                      <td className="px-4 py-2 text-xs tabular-nums">{b.date_depot ?? '-'}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {b.status === 'generated' && (
                          <button
                            type="button"
                            onClick={() => archiveMutation.mutate(b.id)}
                            disabled={archiveMutation.isPending}
                            className="px-2.5 py-1 rounded-md text-xs font-medium bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50"
                          >
                            Enregistrer dans Dropbox
                          </button>
                        )}
                        {b.status === 'uploaded' && (
                          <button
                            type="button"
                            onClick={() => {
                              setDepositDate(todayISO());
                              setDepositFor(b);
                            }}
                            className="px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                          >
                            Marquer déposé
                          </button>
                        )}
                        {b.status === 'completed' && (
                          <span className="text-xs text-emerald-700">
                            Déposé{b.date_depot ? ` le ${b.date_depot}` : ''}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Modale : saisie de la date de dépôt en banque */}
      {depositFor && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4"
          onClick={() => !completeMutation.isPending && setDepositFor(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold">Marquer déposé</h2>
            <p className="text-sm text-slate-500 mt-1">
              Fichier <span className="font-mono text-xs">{depositFor.xml_filename}</span> ({depositFor.banque}).
              Indique la date de dépôt en banque.
            </p>
            <label className="block mt-4">
              <span className="text-sm font-medium text-slate-700">Date de dépôt</span>
              <input
                type="date"
                value={depositDate}
                onChange={(e) => setDepositDate(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDepositFor(null)}
                disabled={completeMutation.isPending}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => completeMutation.mutate({ id: depositFor.id, date_depot: depositDate })}
                disabled={completeMutation.isPending || !depositDate}
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {completeMutation.isPending && <Spinner />}
                Confirmer le dépôt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
